import { domainError, type DomainError } from '../shared/domain-error';
import { err, ok, type Result } from '../shared/result';
import type {
  ActivityId,
  ExecutionResourceId,
  PlanningChangeId,
  ProjectId,
  UserId,
  WeeklyAllocationId,
} from '../shared/ids';
import { PLANNING_ERROR } from './errors';

/**
 * Alteração de planejamento — o registro do que mudou, quem mudou e por quê.
 *
 * **Alterar não pede motivo; passar da jornada, sim.**
 *
 * Enquanto o dia não aconteceu, mexer no plano é o trabalho — remanejar gente entre
 * frentes três vezes numa terça de manhã é planejamento sendo feito, não desvio a
 * explicar. E na conferência, corrigir o que saiu também é dizer a verdade sobre o dia, e
 * não infração. Exigir um texto a cada mudança transformaria a tela num formulário, e o
 * resultado previsível seria "ajuste" digitado dez vezes ao dia: a regra viraria
 * digitação obrigatória, que é a forma mais rápida de esvaziá-la.
 *
 * **RN-070 foi substituída (RN-291).** Ela exigia a justificativa a partir da segunda
 * alteração do dia feita na conferência. O que passou a ser exigido é só a **hora extra**:
 * o total da pessoa no dia, somando todas as frentes e serviços, acima da jornada daquele
 * dia (RN-292). A conta é a da capacidade (`isOvertime`, RN-044/RN-045) — a mesma que o
 * planejamento e o RDO já usam —, e não mora aqui: esta política não decide quando o motivo
 * é exigido, só guarda o que vier.
 *
 * O que continua difícil de burlar está aqui, não na tela:
 *
 * - a sequência é **do dia do recurso**, não da alocação (RN-071). Excluir e recriar a
 *   alocação não a zera, porque ela nunca olhou para a alocação;
 * - **criar não é alterar** (RN-072), mas somar horas numa alocação que já existe é;
 * - **excluir é alterar** (RN-073) — sumir com a linha é a forma mais fácil de mudar o
 *   plano sem parecer que mudou;
 * - gravar o **mesmo valor** não conta e não é registrado (RN-071).
 *
 * **Toda** alteração vira histórico, inclusive as que não pediram motivo: elas continuam
 * respondendo "o que mudou, quando e por quem" (Q12 a Q14) e alimentam o indicador de
 * alterações do painel (RN-293).
 *
 * Tudo aqui é função pura. Quem conta as alterações do dia é o repositório; quem decide
 * se pode gravar é esta política; e quem recusa é o caso de uso, dentro da transação e
 * **antes** de qualquer escrita (RN-075).
 */

/**
 * De onde veio a alteração.
 *
 * Fica no histórico para responder "foi o planejamento ou a conferência?". A sequência do
 * dia é uma só para as duas — "3ª alteração do dia" conta tudo que mexeu naquele dia,
 * venha de onde vier.
 */
export type ChangeSource = 'PLANNING' | 'INSPECTION';

export type ChangeKind = 'HOURS' | 'ACTIVITY' | 'DATE' | 'DELETE';

export const CHANGE_KIND_LABEL: Readonly<Record<ChangeKind, string>> = {
  HOURS: 'Horas',
  ACTIVITY: 'Atividade',
  DATE: 'Dia',
  DELETE: 'Alocação removida',
};

export const CHANGE_SOURCE_LABEL: Readonly<Record<ChangeSource, string>> = {
  PLANNING: 'Planejamento',
  INSPECTION: 'Conferência',
};

export interface PlanningChange {
  readonly id: PlanningChangeId;
  readonly projectId: ProjectId;
  readonly resourceId: ExecutionResourceId;
  /** O dia do recurso — é ele que agrupa o contador (RN-071). */
  readonly date: string;
  /**
   * Sequência **do dia**, contínua a partir de 1.
   *
   * Uma alteração que mexe em dois campos ao mesmo tempo é **uma** alteração: as duas
   * linhas compartilham a sequência. Contar por campo faria trocar atividade e horas de
   * uma vez consumir duas das alterações permitidas.
   */
  readonly sequence: number;
  readonly source: ChangeSource;
  readonly kind: ChangeKind;
  readonly allocationId: WeeklyAllocationId;
  readonly activityId?: ActivityId;
  /** `"quarterHours"`, `"activityId"`, `"date"`, `"allocation"`. */
  readonly field: string;
  readonly from?: string;
  readonly to?: string;
  readonly justification?: string;
  readonly actorUserId: UserId;
  readonly occurredAt: string;
  /**
   * Quando foi feita no aparelho, se veio da fila de quem estava sem sinal (Fase 43, RN-607). A
   * que vale para a ordem e o contador continua sendo `occurredAt`, a do servidor.
   */
  readonly deviceOccurredAt?: string;
}

// ── Justificativa ───────────────────────────────────────────────────────────────

export const MIN_CHANGE_JUSTIFICATION = 10;
export const MAX_CHANGE_JUSTIFICATION = 1000;

/** Letras e dígitos, ignorando acento — o que separa texto de enfeite. */
function meaningfulLength(value: string): number {
  return value.normalize('NFD').replace(/[^\p{Letter}\p{Number}]/gu, '').length;
}

/**
 * A justificativa tem conteúdo? (RN-074)
 *
 * Dez caracteres depois do `trim`, e pelo menos cinco deles com significado. `"..........`"
 * e `"----------"` têm o tamanho pedido e não dizem nada; aceitá-los seria transformar a
 * regra em digitação obrigatória, que é a forma mais rápida de ensinar todo mundo a
 * driblá-la.
 */
export function validateChangeJustification(raw: string | undefined): Result<string, DomainError> {
  const value = (raw ?? '').trim();

  if (value.length < MIN_CHANGE_JUSTIFICATION || meaningfulLength(value) < 5) {
    return err(
      domainError(
        PLANNING_ERROR.JUSTIFICATION_TOO_SHORT,
        `A justificativa precisa de pelo menos ${String(MIN_CHANGE_JUSTIFICATION)} caracteres que digam o motivo.`,
      ),
    );
  }

  if (value.length > MAX_CHANGE_JUSTIFICATION) {
    return err(
      domainError(
        PLANNING_ERROR.JUSTIFICATION_TOO_LONG,
        `A justificativa deve ter no máximo ${String(MAX_CHANGE_JUSTIFICATION)} caracteres.`,
      ),
    );
  }

  return ok(value);
}

// ── A política ──────────────────────────────────────────────────────────────────

export interface ChangeField {
  readonly kind: ChangeKind;
  readonly field: string;
  readonly from?: string;
  readonly to?: string;
}

export interface DecideChangeInput {
  /**
   * Alterações **efetivas** já registradas no dia do recurso, de qualquer fonte (RN-071).
   *
   * Define o **número** desta alteração — a sequência do dia, que é única e contínua.
   */
  readonly previousChangeCount: number;
  /** O que muda de fato. Vazio = nada mudou, e nada é registrado. */
  readonly fields: readonly ChangeField[];
  /** O motivo que veio junto — o da hora extra, quando houve (RN-292). */
  readonly justification?: string;
}

export type ChangeDecision =
  /** Nada mudou: não conta, não grava histórico (RN-071). */
  | { readonly kind: 'NOTHING_CHANGED' }
  /** Permitida: a alteração entra na sequência do dia, com o motivo se houver. */
  | { readonly kind: 'ALLOWED'; readonly sequence: number; readonly justification?: string };

/**
 * Decide o que fazer com a alteração e com que sequência.
 *
 * Devolve a **sequência** junto com a decisão para que o caso de uso não precise contar de
 * novo: quem pergunta "posso?" recebe "pode, e esta é a alteração número N do dia".
 *
 * Não recusa por falta de motivo (RN-291): quem exige a justificativa é a conta da hora
 * extra, antes de chegar aqui. O motivo que vier junto continua sendo guardado — a
 * justificativa de uma hora extra (RN-045) chega por aqui e é gravada com a alteração.
 */
export function decideChange(input: DecideChangeInput): ChangeDecision {
  if (input.fields.length === 0) return { kind: 'NOTHING_CHANGED' };

  const justification = input.justification === undefined ? undefined : input.justification.trim();
  return {
    kind: 'ALLOWED',
    sequence: input.previousChangeCount + 1,
    ...(justification === undefined || justification === '' ? {} : { justification }),
  };
}

// ── Detecção do que mudou ───────────────────────────────────────────────────────

export interface AllocationSnapshot {
  readonly quarterHours: number;
  readonly activityId: ActivityId;
  readonly date: string;
}

/**
 * O que mudou entre dois estados da alocação (RN-071, RN-077).
 *
 * Campo com o mesmo valor não entra: gravar `9h` sobre `9h` não é alteração, não conta no
 * contador e não vira linha no histórico. O `de → para` sai daqui, e é ele que garante
 * que nenhum valor anterior seja sobrescrito em silêncio.
 */
export function detectChangedFields(
  before: AllocationSnapshot,
  after: AllocationSnapshot,
): ChangeField[] {
  const fields: ChangeField[] = [];

  if (before.quarterHours !== after.quarterHours) {
    fields.push({
      kind: 'HOURS',
      field: 'quarterHours',
      from: String(before.quarterHours),
      to: String(after.quarterHours),
    });
  }
  if (before.activityId !== after.activityId) {
    fields.push({
      kind: 'ACTIVITY',
      field: 'activityId',
      from: before.activityId,
      to: after.activityId,
    });
  }
  if (before.date !== after.date) {
    fields.push({ kind: 'DATE', field: 'date', from: before.date, to: after.date });
  }

  return fields;
}

/** Excluir a alocação é alteração (RN-073) — some do plano, e sumir é mudar. */
export function deletionField(snapshot: AllocationSnapshot): ChangeField {
  return {
    kind: 'DELETE',
    field: 'allocation',
    from: String(snapshot.quarterHours),
  };
}

/** Chave natural: uma linha por (recurso, dia, sequência, campo). */
export function planningChangeNaturalKey(
  resourceId: ExecutionResourceId,
  date: string,
  sequence: number,
  field: string,
): string {
  return `${resourceId}|${date}|${String(sequence)}|${field}`;
}

export function createPlanningChange(input: {
  id: PlanningChangeId;
  projectId: ProjectId;
  resourceId: ExecutionResourceId;
  date: string;
  sequence: number;
  source: ChangeSource;
  change: ChangeField;
  allocationId: WeeklyAllocationId;
  activityId?: ActivityId;
  justification?: string;
  actorUserId: UserId;
  now: string;
}): PlanningChange {
  return {
    id: input.id,
    projectId: input.projectId,
    resourceId: input.resourceId,
    date: input.date,
    sequence: input.sequence,
    source: input.source,
    kind: input.change.kind,
    allocationId: input.allocationId,
    field: input.change.field,
    actorUserId: input.actorUserId,
    occurredAt: input.now,
    ...(input.activityId === undefined ? {} : { activityId: input.activityId }),
    ...(input.change.from === undefined ? {} : { from: input.change.from }),
    ...(input.change.to === undefined ? {} : { to: input.change.to }),
    ...(input.justification === undefined ? {} : { justification: input.justification }),
  };
}

/** Ordem do histórico: do mais antigo para o mais novo, como se lê uma conversa. */
export function comparePlanningChanges(a: PlanningChange, b: PlanningChange): number {
  return a.sequence - b.sequence || a.field.localeCompare(b.field);
}
